import { HistoryEntry, IPagination } from ".";
import { Agency, AgencyUser, Note, Review } from "./agency";

import { requestStatusTypeMap } from "@shared/lib/constants/index";
import { CreatorProfileJobStatus } from "./briefInfluencerV2";

export enum INFLUENCER_ACCOUNT_STATUS {
  BLACKLISTED = "BLACKLISTED",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export interface ITopPerformingContent {
  title?: string;
  caption?: string;
  url?: string;
  link?: string;
  permalink?: string;
  views?: number | string;
  view_count?: number | string;
  views_vs_baseline?: number | string;
  views_vs_baseline_pct?: number | string;
  engagement_rate?: number | string;
  er?: number | string;
  er_vs_baseline?: number | string;
  er_vs_baseline_pct?: number | string;
  description?: string;
  what_worked?: string;
}

export interface ICreatorContentAnalysis {
  content_style?: string[] | string;
  what_works?: string[] | string;
  what_to_avoid?: string[] | string;
  top_performing_content?: ITopPerformingContent[];
}

export interface ICreatorInsights {
  creator_matchmaking_description: string;
  content_style?: string[] | string;
  what_works?: string[] | string;
  what_to_avoid?: string[] | string;
  top_performing_content?: ITopPerformingContent[];
  content_analysis?: ICreatorContentAnalysis;

  overall_brand_fit: {
    best_fit_brand_categories: string[];
    brand_fit_summary: string;
  };

  overall_safety: {
    overall_safety_score: number;
    safety_notes: string[];
  };

  overall_scores: {
    authenticity_score: number;
    relatability_score: number;
    content_quality_score: number;
    trust_score: number;
    audience_clarity_score: number;
    natural_brand_integration_score: number;
    commercial_readiness_score: number;
    premium_brand_fit_score: number;
    mass_market_brand_fit_score: number;
    repeatability_score: number;
    creator_brand_match_usefulness_score: number;
  };

  overall_creator_persona: {
    authenticity_summary: string;
  };
}

export enum SocialMediaJobStatuses {
  Queued = "queued",
  WaitingForPrerequisites = "waiting_for_prerequisites",
  CheckingPrerequisites = "checking_prerequisites",
  QueuedForCollectingMedia = "queued_for_collecting_media",
  CollectingMedia = "collecting_media",
  Uploading = "uploading",
  "Running-Gemini" = "running_gemini",
  Completed = "completed",
  Failed = "failed",
}

export interface SocialMedia {
  _id?: string;
  platform: string;
  username: string;
  link: string;
  followers: number;
  profilePicUrl?: string;
  searchV1?: {
    fullName: string;
    followerCount: number;
  };
  creatorProfileJob?: {
    config: {
      maxPosts: number;
      minUploads: number;
      aggregateCount: number;
    };
    progress: {
      total: number;
      uploaded: number;
      failed: number;
      pending: number;
    };
    failure?: {
      code: CreatorProfileJobStatus;
      message: string;
      retryable: boolean;
    };
    status: SocialMediaJobStatuses;
    createdAt: string;
    updatedAt: string;
    completedAt: string;
    creatorProfileId: string;
    creatorProfile?: {
      socialMediaObjectId: string;
      _id: string;
      aiAnalysis?: ICreatorInsights;
    };
  };
}

export interface SocialMediaEntry {
  platform: string;
  userName: string;
  socialMedia: SocialMedia;
  socialMediaLinkageId: string;
}

export interface Influencer {
  _id: string;
  influencerId: string;
  firstName: string;
  lastName: string;
  profilePicUrl: string;
  history: HistoryEntry[];
  type: InfluencerType;
  isPrimaryContact: boolean;
  phoneNumber: string;
  countryCode: string;
  emailId: string;
  contacts: InfluencerContact[];
  isAgency: boolean;
  agency?: Agency;
  reviews: Review[]; // Adjust type if reviews have a specific structure
  socialMedias: SocialMediaEntry[];
  createdAt: string;
  updatedAt: string;
  note: Note;
}

export enum InfluencerType {
  self = "Self",
  manager = "Manager",
  agency = "Agency",
}
export enum GenderEnum {
  male = "Male",
  female = "Female",
  others = "Others",
}

export enum SocialMediaPlatform {
  Instagram = "instagram",
  YouTube = "youtube",
  LinkedIn = "linkedin",
  TikTok = "tiktok",
}
export interface InfluencerContact {
  name: string;
  type: InfluencerType;
  phoneNumber?: string;
  emailId?: string;
  isPrimaryContact: boolean;
  influencerContactId: string;
  countryCode?: string;
  agency?: Agency;
  agencyUserId?: string;
  agencyUser: AgencyUser;
  isVerified?: boolean;
  status: CONTACT_STATUS;
}

export enum CONTACT_STATUS {
  Verified = "Verified",
  Unverified = "Not-Verified",
  Expired = "Expired",
  INVALID = "Invalid",
}

export interface InfluencerRemoveContact extends Omit<
  InfluencerContact,
  "agency"
> {
  agency?: string;
}

export interface CreateInfluencerRequest {
  profilePicUrl?: string;
  firstName: string;
  lastName: string;
  type: InfluencerType;
  phoneNumber?: string;
  emailId?: string;
  countryCode?: string;
}

export interface ICreateInfluencerRequest {
  profilePicUrl?: string;
  firstName: string;
  lastName: string;
  type: InfluencerType;
  phoneNumber?: string;
  emailId?: string;
  countryCode?: string;
  gender: GenderEnum;
  locationId: string;
  category: string[];
  agencyId?: string;
  attachSocialMediaObjectId?: string;
}

export interface IAgencyInfluencerRequestFacets {
  [key: string]: {
    _id: string;
    count: number;
    agency: {
      _id: string;
      name: string;
    };
  }[];
}

export interface IInfluencers {
  _id: string;
  influencerId: string;
  firstName: string;
  lastName: string;
  profilePicUrl: string;
  history: HistoryEntry[];
  type: InfluencerType;
  isPrimaryContact: boolean;
  phoneNumber: string;
  countryCode: string;
  emailId: string;
  contacts: InfluencerContact[];
  isAgency: boolean;
  agency?: Agency;
  gender: GenderEnum;
  reviews: Review[]; // Adjust type if reviews have a specific structure
  socialMedias: SocialMediaEntry[];
  createdAt: string;
  updatedAt: string;
  category: string[];
  location: string[];
  locationGooglePlaceId: string;
  locationPoint: LocationPoint;
  avgReview: number;
  agencyUser?: AgencyUser;
}

interface LocationPoint {
  type: string;
  coordinates: [number, number]; // [longitude, latitude]
}

export interface GetInfluencers {
  data: IInfluencers[];
  pagination: IPagination;
}

export interface IInfluencerFacets {
  [key: string]: {
    _id: string;
    count: number;
  }[];
}
export interface ILocationFacet {
  description: string;
  locationId: string;
}

export interface IInfluencer {
  _id: string;
  isArchived: boolean;
  accountStatus: INFLUENCER_ACCOUNT_STATUS;
  influencerId: string;
  firstName: string;
  lastName: string;
  profilePicUrl?: string;
  history: HistoryEntry[];
  type: InfluencerType;
  isPrimaryContact: boolean;
  phoneNumber: string;
  countryCode: string;
  emailId: string;
  contacts: InfluencerContact[];
  isAgency: boolean;
  agency?: Agency;
  gender: GenderEnum;
  reviews: Review[]; // Adjust type if reviews have a specific structure
  socialMedias: SocialMediaEntry[];
  createdAt: string;
  updatedAt: string;
  category: string[];
  location: string[];
  locationGooglePlaceId: string;
  locationPoint: LocationPoint;
  avgReview: number;
  locationObj: {
    description: string;
    locationId: string;
  };
  note: Note;
  linkedAgencies: {
    _id: string;
    name: string;
    profilePicUrl: string;
  }[];
}

export interface SocialMediaSearch {
  name: string;
  profilePicUrl: string;
  username: string;
  isLinkedToInfluencer: boolean;
  linkedInfluencer?: IInfluencer;
  followers: number;
  link: string;
}

export interface ICreateBucketRequest {
  bucketName: string;
  bucketDescription: string;
  bucketCategory: string[];
}

export interface IBucketFacets {
  [key: string]: {
    _id: string;
    count: number;
  }[];
}

export interface IBuckRequest {
  searchQuery?: string;
  pageNo?: number;
  limit?: number;
  category?: string[];
  minInfluencerCount?: number;
  categories?: string[];
}

export interface Pagination {
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export interface IBucket {
  _id: string;
  name: string;
  socialMediaNames: string[];
  influencerNames: string[];
  history: HistoryEntry[];
  description: string;
  category: string[];
  createdBy: string;
  createdByModelName: string;
  createdByType: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  score: number;
  bucketId: string;
  campaign: string;
}

export interface IBucketResponse {
  data: IBucket[];
  pagination: Pagination;
}

export interface SocialMediaInfluencer {
  influencer: IInfluencer;
  isInflunecerFound: boolean;
  socialMedia: SocialMedia[];
  isSocialMediaFound: boolean;
  isErrorFetchingSocialMedia: boolean;
}

export type SocialMediaInfluencerRequest =
  | { influencerId: string }
  | { socialMediaObjectId: string }
  | {
      username: {
        platform: string;
        socialMediaUsername: string;
      };
      toOpenInfluencer?: boolean;
    };

export interface ICreateInfluencerRequest {
  profilePicUrl?: string;
  firstName: string;
  lastName: string;
  type: InfluencerType;
  phoneNumber?: string;
  emailId?: string;
  countryCode?: string;
  gender: GenderEnum;
  locationId: string;
  category: string[];
  socialMedias: SocialMedia[];
}

export interface IInfluencerRequestAdd extends ICreateInfluencerRequest {
  status: keyof typeof requestStatusTypeMap;
  remarks?: string;
  locationObj?: {
    description?: string;
    locationId?: string;
  };
  agency: {
    name?: string;
    _id?: string;
    agencyId?: string;
  };
  agencyUser?: AgencyUser;
  influencerId: string;
  _id: string;
}

export interface IInfluencerRequests {
  data: IInfluencerRequestAdd[];
  pagination: IPagination;
}

export interface IInfluencerContactVerification {
  _id: string;
  influencerName: string;
  influencerContactId: string;
  influencerContact: InfluencerContact;
  influencerId: string;
  influencer: {
    _id: string;
    firstName: string;
    lastName: string;
    profilePicUrl: string;
    influencerId: string;
  };
  status: "Pending" | "Resolved";
  resolutionRemarks?: string;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    profilePicUrl: string;
    id: string;
  };
  createdByModelName: string;
  createdByType: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface IInfluencerContactVerificationResponse {
  data: IInfluencerContactVerification[];
  pagination: IPagination;
}

export interface IAddSocialMediaBulkRequest {
  addBy: {
    csv?: string;
    platform_username?: {
      platform: string;
      username: string;
    };
  };
}

export interface IAddSocialMediaBulkResponseRow {
  rowNumber: number;
  username: string;
  platform: string;
  status: "failed" | "success";
  socialMediaObjectId?: string;
  message?: string;
}

export interface IYoutubeScrapeResponseItem {
  trackedQuery: string;
  channelName: string;
  channelId: string;
  channelUrl: string;
  channelSubscribers: number;
  videoTitle: string;
  videoType: string;
  durationSeconds: number;
  views: number;
  likes: number;
  comments: number;
  engagementRate: number;
  publishedAt: string;
  videoUrl: string;
  tags: string[];
  fullDescription: string;
  channelProfilePicUrl: string;
}

export interface IYoutubeScrapeResponse {
  items: IYoutubeScrapeResponseItem[];
  nextPageToken: string;
  pageInfo: {
    totalResults: number;
    resultsPerPage: number;
  };
}

export interface IYoutubeScrapeRequest {
  query: string[];
  publishedFrom: string;
  publishedTo: string;
  maxResults: number;
}
